import BackToCPQButton from "../../Components/backToCPQButton";
import PreviousPageButtonCPQ from "../../Components/PreviousPageButtonCPQ";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Divider,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    FilterAlt as FilterIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";
import apiClient from "../../Helpers/apiClient";

function LevelControlPriceTable() {
    const [levelControls, setLevelControls] = useState([]);
    const [filteredLevelControls, setFilteredLevelControls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedControl, setSelectedControl] = useState(null);
    const [formData, setFormData] = useState({
        id: 0,
        description: "",
        price: 0,
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    // Sorting states
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");
    // Filtering states
    const [filterText, setFilterText] = useState("");
    const [filterField, setFilterField] = useState("all");

    const navigate = useNavigate();

    useEffect(() => {
        fetchLevelControls();
    }, []);

    // Apply filters whenever levelControls or filter settings change
    useEffect(() => {
        applyFilters();
    }, [levelControls, filterText, filterField]);

    const fetchLevelControls = async () => {
        setLoading(true);
        try {
            console.log("Fetching level controls...");

            const response = await apiClient.get("/api/PumpStationPriceTables/LevelControlPrices");

            console.log("Response received successfully!");
            console.log("Data type:", typeof response.data);
            console.log("Is array:", Array.isArray(response.data));

            if (Array.isArray(response.data)) {
                setLevelControls(response.data);
                console.log(`Successfully loaded ${response.data.length} level controls`);
            } else {
                console.warn("Response is not an array:", response.data);
                setLevelControls([]);
            }

            setError(null);
        } catch (err) {
            console.error("Error fetching level controls:", err);
            setError(`Failed to load data: ${err.message}`);
            setLevelControls([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (control = null) => {
        if (control) {
            setFormData({
                id: control.id || control.Id || 0,
                description: control.description || control.Description || "",
                price: control.price || control.Price || 0,
            });
            setSelectedControl(control);
        } else {
            setFormData({
                id: 0,
                description: "",
                price: 0,
            });
            setSelectedControl(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleOpenDeleteDialog = (control) => {
        setSelectedControl(control);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedControl(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === "price" ? parseFloat(value) || 0 : value,
        });
    };

    const handleSubmit = async () => {
        try {
            // Convert camelCase to PascalCase to match C# model property names
            const payload = {
                Id: formData.id,
                Description: formData.description,
                Price: formData.price,
                Quotations: [],
            };

            let response;
            if (selectedControl) {
                // Update existing record
                response = await apiClient.put(
                    `/api/PumpStationPriceTables/LevelControlPrices/${payload.Id}`,
                    payload
                );
                setSnackbar({
                    open: true,
                    message: "Level control updated successfully!",
                    severity: "success",
                });
            } else {
                // Create new record
                response = await apiClient.post("/api/PumpStationPriceTables/LevelControlPrices", payload);
                setSnackbar({
                    open: true,
                    message: "Level control created successfully!",
                    severity: "success",
                });
            }

            handleCloseDialog();
            fetchLevelControls();
        } catch (err) {
            console.error("Error saving level control:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to save. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await apiClient.delete(
                `/api/PumpStationPriceTables/LevelControlPrices/${selectedControl.id || selectedControl.Id}`
            );
            setSnackbar({
                open: true,
                message: "Level control deleted successfully!",
                severity: "success",
            });
            handleCloseDeleteDialog();
            fetchLevelControls();
        } catch (err) {
            console.error("Error deleting level control:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to delete. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Sorting functionality
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const getSortedData = (data) => {
        if (!data.length) return [];

        return [...data].sort((a, b) => {
            // Handle the ID/modelNo column specially
            if (orderBy === "modelNo") {
                const idA = parseInt(a.id || a.Id || 0);
                const idB = parseInt(b.id || b.Id || 0);
                return order === "asc" ? idA - idB : idB - idA;
            }

            // For any other column, get the property with both camelCase and PascalCase checks
            let valueA, valueB;

            // Try camelCase first, then PascalCase if not found
            if (orderBy === "description") {
                valueA = a.description !== undefined ? a.description : a.Description;
                valueB = b.description !== undefined ? b.description : b.Description;
            } else if (orderBy === "price") {
                valueA = a.price !== undefined ? a.price : a.Price;
                valueB = b.price !== undefined ? b.price : b.Price;

                // Convert to numbers for price comparison
                valueA = parseFloat(valueA) || 0;
                valueB = parseFloat(valueB) || 0;
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }

            // For non-numeric values, convert to strings and compare
            if (typeof valueA !== "number") {
                valueA = String(valueA || "").toLowerCase();
                valueB = String(valueB || "").toLowerCase();
                return order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }

            // For numeric values
            return order === "asc" ? valueA - valueB : valueB - valueA;
        });
    };

    // Filtering functionality
    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    const handleFilterFieldChange = (field) => {
        setFilterField(field);
    };

    const clearFilter = () => {
        setFilterText("");
        setFilterField("all");
    };

    const applyFilters = () => {
        if (!filterText.trim()) {
            setFilteredLevelControls(levelControls);
            return;
        }

        const searchTerm = filterText.toLowerCase().trim();

        const filtered = levelControls.filter((control) => {
            // Format model number for filtering (LC-001, LC-002, etc.)
            const modelNo = `LC-${(control.id || control.Id || 0).toString().padStart(3, "0")}`;

            // Handle both camelCase and PascalCase property names from API
            const description = control.description || control.Description || "";
            const price = control.price || control.Price || 0;

            if (filterField === "all") {
                return (
                    modelNo.toLowerCase().includes(searchTerm) ||
                    String(control.id || control.Id || "").includes(searchTerm) ||
                    description.toLowerCase().includes(searchTerm) ||
                    String(price).includes(searchTerm)
                );
            }

            // Filter by specific field
            if (filterField === "modelNo") return modelNo.toLowerCase().includes(searchTerm);
            if (filterField === "id") return String(control.id || control.Id || "").includes(searchTerm);
            if (filterField === "description") return description.toLowerCase().includes(searchTerm);
            if (filterField === "price") return String(price).includes(searchTerm);

            return false;
        });

        setFilteredLevelControls(filtered);
    };

    // Prepare data for rendering
    const displayData = getSortedData(filteredLevelControls);

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                padding: 4,
                paddingTop: 2,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            <Typography variant="h4" gutterBottom align="center">
                Level Control Price Table
            </Typography>

            <Paper
                sx={{
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 2,
                        alignItems: "center",
                    }}
                >
                    {/* Filter */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "60%",
                        }}
                    >
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            value={filterText}
                            onChange={handleFilterChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: filterText ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearFilter} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Tooltip title="Filter by field">
                            <Box>
                                <IconButton
                                    color={filterField !== "all" ? "primary" : "default"}
                                    aria-label="Filter options"
                                    onClick={(e) => {
                                        e.currentTarget.nextElementSibling.style.display =
                                            e.currentTarget.nextElementSibling.style.display === "none"
                                                ? "flex"
                                                : "none";
                                    }}
                                >
                                    <FilterIcon />
                                </IconButton>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        zIndex: 10,
                                        bgcolor: "background.paper",
                                        boxShadow: 3,
                                        borderRadius: 1,
                                        p: 1,
                                        display: "none",
                                        flexDirection: "column",
                                        gap: 0.5,
                                    }}
                                >
                                    {[
                                        { id: "all", label: "All Fields" },
                                        { id: "modelNo", label: "ID" },
                                        {
                                            id: "description",
                                            label: "Description",
                                        },
                                        { id: "price", label: "Price" },
                                    ].map((option) => (
                                        <Button
                                            key={option.id}
                                            size="small"
                                            variant={filterField === option.id ? "contained" : "text"}
                                            onClick={() => {
                                                handleFilterFieldChange(option.id);
                                                // Hide the filter dropdown
                                                document.querySelector(
                                                    '[aria-label="Filter options"]'
                                                ).nextElementSibling.style.display = "none";
                                            }}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Tooltip>
                        {filterField !== "all" && (
                            <Typography variant="caption" sx={{ ml: 1 }}>
                                Filtering by:{" "}
                                {filterField === "modelNo"
                                    ? "ID"
                                    : filterField === "id"
                                    ? "ID"
                                    : filterField === "description"
                                    ? "Description"
                                    : filterField === "price"
                                    ? "Price"
                                    : ""}
                            </Typography>
                        )}
                    </Box>

                    {/* Add button */}
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Item
                    </Button>
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            sx={{
                                flex: 1,
                                overflow: "auto",
                            }}
                        >
                            <Table stickyHeader aria-label="level control price table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "modelNo"}
                                                direction={orderBy === "modelNo" ? order : "asc"}
                                                onClick={() => handleRequestSort("modelNo")}
                                            >
                                                <strong>ID</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "description"}
                                                direction={orderBy === "description" ? order : "asc"}
                                                onClick={() => handleRequestSort("description")}
                                            >
                                                <strong>Description</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "price"}
                                                direction={orderBy === "price" ? order : "asc"}
                                                onClick={() => handleRequestSort("price")}
                                            >
                                                <strong>Price</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Actions</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                {filterText
                                                    ? "No matches found. Try a different search."
                                                    : "No data available. Add your first level control."}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayData.map((control) => (
                                            <TableRow key={control.id || control.Id} hover>
                                                <TableCell>
                                                    LC-
                                                    {(control.id || control.Id).toString().padStart(3, "0")}
                                                </TableCell>
                                                <TableCell>
                                                    {control.description || control.Description}
                                                </TableCell>
                                                <TableCell>
                                                    $
                                                    {typeof (control.price || control.Price) === "number"
                                                        ? (control.price || control.Price).toFixed(2)
                                                        : "0.00"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(control)}
                                                        aria-label="edit"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleOpenDeleteDialog(control)}
                                                        aria-label="delete"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {filterText
                                    ? `Showing ${displayData.length} of ${levelControls.length} items`
                                    : `Total: ${levelControls.length} items`}
                            </Typography>
                            {filterText && (
                                <Button size="small" startIcon={<ClearIcon />} onClick={clearFilter}>
                                    Clear Filter
                                </Button>
                            )}
                        </Box>
                    </>
                )}
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <BackToCPQButton />
                <PreviousPageButtonCPQ />
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedControl ? "Edit Level Control" : "Add New Level Control"}</DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                    helperText="e.g., Ultrasonic Level Sensor"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Price"
                                    name="price"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.price}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {selectedControl ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this level control? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default LevelControlPriceTable;
